import React, { Children, cloneElement, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { FilterAlt, FilterAltOff, Search, SearchOff, TuneOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import {
    Badge,
    Box,
    Button,
    Checkbox,
    Chip,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Grid2 as Grid,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Popover,
    Radio,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import _ from 'lodash';
import { AspyreDatePicker as DatePicker } from 'components/common/InputFields';

import PropTypes from 'prop-types';
import Spinner from 'ui-component/Spinner';
import { GLOBAL_DATE_FORMAT } from 'constant';
import { useFormik } from 'formik';
import * as yup from 'yup';
import useSnackbar from 'hooks/useSnackbar';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useDispatch } from 'store';
import {
    setLocationFilter,
    setPatientFilter,
    setEmployeeFilter,
    setPatientPage,
    setLocationPage,
    setEmployeePage
} from 'store/slices/filters';

export const getFilterValues = (filters) => {
    const initialValues = {};

    filters.forEach((filter) => {
        initialValues[filter.id] = filter.default;
    });

    return initialValues;
};

const validationSchema = yup.object().shape({
    date: yup.date().typeError('Please enter a valid date').nullable()
});

function Datepicker(props) {
    const { value, onChange, id, containerProps, sx, ...rest } = props;
    // const [date, setDate] = useState(value?.[id] || null);

    const formik = useFormik({
        initialValues: {
            date: value?.[id] || null
        },
        validationSchema
    });

    useEffect(() => {
        onChange(id || 'date', formik.values.date);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.date]);

    return (
        <Box {...containerProps}>
            <DatePicker
                id={id}
                name={id}
                inputFormat={GLOBAL_DATE_FORMAT}
                value={formik.values.date}
                onChange={(val) => formik.setFieldValue('date', val)}
                renderInput={(props) => (
                    <TextField
                        {...props}
                        sx={sx}
                        fullWidth
                        size="small"
                        error={Boolean(formik.errors.date)}
                        helperText={formik.errors.date}
                        slotProps={{
                            htmlInput: {
                                // eslint-disable-next-line react/destructuring-assignment, react/prop-types
                                ...props.inputProps
                            }
                        }}
                    />
                )}
                {...rest}
            />
        </Box>
    );
}

function CListGroup({ name, options, value, multiple, groupBy = false, groupByKey, onChange, radio = false, variant = 'classic' }) {
    const [selected, setSelected] = useState(value ?? (radio ? null : []));

    const [searchText, setSearchText] = useState('');

    const isMasterDetail = variant === 'master-detail';

    let selectedCount = 0;
    if (Array.isArray(selected)) {
        selectedCount = selected.length;
    } else if (selected !== null && selected !== undefined) {
        selectedCount = 1;
    }

    const handleClear = () => setSelected(radio ? null : []);

    const renderOptionLabel = (label) => (
        <Tooltip
            title={label}
            slotProps={{
                popper: { disablePortal: true }
            }}
            placement="bottom-end"
        >
            <Typography component="span" variant={isMasterDetail ? 'body2' : undefined} noWrap sx={{ display: 'block' }}>
                {label}
            </Typography>
        </Tooltip>
    );

    const optionRowSx = isMasterDetail
        ? {
              width: '100%',
              mx: 0,
              px: 1,
              py: 0.25,
              borderRadius: 2,
              transition: 'background-color 120ms ease',
              '&:hover': { bgcolor: 'action.hover' },
              '& .MuiFormControlLabel-label': {
                  overflow: 'hidden'
              }
          }
        : {
              width: '100%',
              '& .MuiFormControlLabel-label': {
                  overflow: 'hidden'
              }
          };

    const handleCheck = (id) => {
        if (multiple) {
            const idx = selected.findIndex((i) => i === id);
            if (idx < 0) {
                setSelected([...selected, id]);
            } else {
                setSelected((val) => val?.filter((i) => i !== id));
            }
        } else {
            setSelected(id);
        }
    };

    useEffect(() => {
        onChange(selected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const iterable = searchText ? options?.filter((i) => i?.name?.toLowerCase()?.includes(searchText.toLowerCase())) : options;

    const groupedBy = groupBy && groupByKey ? Object.groupBy(iterable, ({ location }) => location?.name) : null;

    const renderOptions = () => {
        if (isMasterDetail && iterable?.length === 0) {
            return (
                <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ py: 6, color: 'text.secondary' }}>
                    {searchText ? <SearchOff sx={{ fontSize: 32 }} /> : <FilterAltOff sx={{ fontSize: 32 }} />}
                    <Typography variant="body2">{searchText ? `No matches for "${searchText}"` : 'No options available'}</Typography>
                </Stack>
            );
        }

        if (groupBy && groupByKey) {
            return (
                <FormGroup>
                    {Object.keys(groupedBy).map((category) => (
                        <Stack key={category}>
                            {isMasterDetail ? (
                                <Typography variant="subtitle2" color="text.secondary" sx={{ px: 1, pt: 1.5, pb: 0.5 }}>
                                    {category}
                                </Typography>
                            ) : (
                                <Typography sx={{ padding: '8px 0px', color: '#999999' }}>{category}</Typography>
                            )}
                            {groupedBy[category].map((i) => (
                                <FormControlLabel
                                    key={i.id}
                                    control={
                                        radio ? (
                                            <Radio
                                                color="primary"
                                                size="small"
                                                checked={selected === i.id}
                                                onChange={() => handleCheck(i.id)}
                                                sx={{ mt: 0.2 }}
                                            />
                                        ) : (
                                            <Checkbox
                                                color="primary"
                                                size="small"
                                                checked={multiple ? selected?.includes(i.id) : selected === i.id}
                                                onChange={() => handleCheck(i.id)}
                                                sx={{ mt: 0.2 }}
                                            />
                                        )
                                    }
                                    label={renderOptionLabel(i.name)}
                                    sx={optionRowSx}
                                />
                            ))}
                        </Stack>
                    ))}
                </FormGroup>
            );
        }

        return (
            <FormGroup>
                {iterable?.map((i) => (
                    <FormControlLabel
                        key={i.id}
                        control={
                            radio ? (
                                <Radio
                                    color="primary"
                                    size="small"
                                    checked={selected === i.id}
                                    onChange={() => handleCheck(i.id)}
                                    sx={{ mt: 0.2 }}
                                />
                            ) : (
                                <Checkbox
                                    color="primary"
                                    size="small"
                                    checked={multiple ? selected?.includes(i.id) : selected === i.id}
                                    onChange={() => handleCheck(i.id)}
                                    sx={{ mt: 0.2 }}
                                />
                            )
                        }
                        label={renderOptionLabel(`${i.name}${_.has(i, 'isActive') && !i?.isActive ? ' (Inactive)' : ''}`)}
                        sx={optionRowSx}
                    />
                ))}
            </FormGroup>
        );
    };

    const renderSearch = () => {
        if (!(options?.length > 15)) return null;

        return (
            <TextField
                type="search"
                placeholder="Search..."
                size="small"
                value={searchText}
                sx={
                    isMasterDetail
                        ? {
                              mt: 1.5,
                              width: '100%',
                              '& .MuiOutlinedInput-input': {
                                  padding: '10px 5px'
                              }
                          }
                        : {
                              mt: 1,
                              maxWidth: 'calc(100% / 1.5)',
                              '& fieldset': {
                                  borderRadius: 1
                              },
                              '& .MuiOutlinedInput-input': {
                                  padding: '10px 5px'
                              }
                          }
                }
                onChange={(event) => setSearchText(event.target.value)}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        )
                    },
                    htmlInput: {
                        sx: { height: 15 }
                    }
                }}
            />
        );
    };

    if (!isMasterDetail) {
        return (
            <Box sx={{ p: 1, height: 380, overflowY: 'auto' }}>
                <Stack sx={{ mb: 1 }}>
                    <Typography variant="subtitle1">{name}</Typography>
                    {options?.length > 15 && <Box sx={{ position: 'relative', m: 0, p: 0 }}>{renderSearch()}</Box>}
                </Stack>
                {renderOptions()}
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, height: 380, overflowY: 'auto' }}>
            <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {name}
                </Typography>
                {/* Height is reserved so selecting the first option doesn't shift the list below */}
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minHeight: 24 }}>
                    {selectedCount > 0 && (
                        <>
                            <Typography variant="caption" color="primary.main">
                                {selectedCount} selected
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                                •
                            </Typography>
                            <Tooltip title={`Clear ${name} selections`}>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleClear}
                                    sx={{
                                        minWidth: 0,
                                        px: 0.75,
                                        py: 0.25,
                                        fontSize: 12,
                                        lineHeight: 1.66,
                                        textTransform: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        color: 'text.secondary',
                                        '&:hover': { bgcolor: 'transparent', color: 'error.main', textDecoration: 'underline' }
                                    }}
                                >
                                    Clear
                                </Button>
                            </Tooltip>
                        </>
                    )}
                </Stack>
                {renderSearch()}
            </Box>
            {renderOptions()}
        </Box>
    );
}

const objectHasValue = (obj) => {
    let counter = 0;
    Object.values(obj).forEach((i) => {
        if (i?.length > 0 || Object?.keys(i || {})?.length > 0 || Number?.isInteger(i)) {
            counter += 1;
        }
    });
    return counter;
};

const getActiveFilterCount = (values = {}) =>
    Object.entries(values || {}).filter(([key, value]) => {
        if (key === 'search') return false;
        if ((key === 'startDate' || key === 'endDate') && value) return true;
        if (key === 'is_expired' && value) return true;
        return value && value.length > 0;
    }).length;

// Mirrors the real panel layout/dimensions so the popover doesn't jump when content arrives
function FilterSkeleton({ hasLists }) {
    return (
        <Stack direction={{ xs: 'column', sm: 'row' }}>
            {hasLists && (
                <Stack
                    direction={{ xs: 'row', sm: 'column' }}
                    spacing={1}
                    sx={{
                        width: { xs: '100%', sm: 200 },
                        flexShrink: 0,
                        p: 1.5,
                        borderColor: 'divider',
                        borderRight: { sm: '1px solid' },
                        borderBottom: { xs: '1px solid', sm: 'none' },
                        overflow: 'hidden'
                    }}
                >
                    {[65, 85, 55, 70].map((width) => (
                        <Skeleton
                            key={width}
                            variant="rounded"
                            height={32}
                            width={{ xs: 96, sm: `${width}%` }}
                            sx={{ borderRadius: 2, flexShrink: 0 }}
                        />
                    ))}
                </Stack>
            )}
            <Box sx={{ flex: 1, minWidth: 0, p: 2, height: 380, overflow: 'hidden' }}>
                <Skeleton variant="text" width="45%" height={30} />
                <Skeleton variant="text" width="25%" height={18} />
                <Skeleton variant="rounded" height={38} sx={{ borderRadius: 2, my: 1.5 }} />
                {[70, 85, 60, 75, 80, 65].map((width) => (
                    <Stack key={width} direction="row" alignItems="center" spacing={1.25} sx={{ py: 0.5 }}>
                        <Skeleton variant="rounded" width={18} height={18} sx={{ borderRadius: 0.75, flexShrink: 0 }} />
                        <Skeleton variant="text" width={`${width}%`} height={24} />
                    </Stack>
                ))}
            </Box>
        </Stack>
    );
}

FilterSkeleton.propTypes = {
    hasLists: PropTypes.bool
};

const Root = forwardRef(
    (
        {
            value,
            filters,
            onChange,
            isLoading,
            children,
            validationSchema,
            dateRageKeys = ['fromDate', 'toDate'],
            noFilterIcon = false,
            disableSearch = false,
            label = 'Search...',
            placeholder = '',
            variant = 'classic'
        },
        refs
    ) => {
        const { resetRef, countRef } = refs || {};

        const isMasterDetail = variant === 'master-detail';

        const { notify } = useSnackbar();
        const initialFilterValues = getFilterValues(filters);
        const [indicator, setIndicator] = useState(0);
        const [filterAnchor, setFilterAnchor] = useState(null);
        const [filterInputs, setFilterInputs] = useState(initialFilterValues);
        const [listKey, setListKey] = useState(0);
        const [activeGroupId, setActiveGroupId] = useState(null);
        const prevfilters = useSelector((state) => state.filters);
        const [firstDate, secondDate] = dateRageKeys;

        const [errors, setErrors] = useState({});

        const { pathname, state } = useLocation();

        const from = state?.from;

        const dispatch = useDispatch();

        useEffect(() => {
            setIndicator(getActiveFilterCount(value || initialFilterValues));
            setErrors({});
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [value]);

        useEffect(() => {
            if (pathname === '/patient' && from !== 'edit-patient') {
                dispatch(
                    setPatientFilter({
                        active: [],
                        locations: [],
                        search: ''
                    })
                );
                dispatch(
                    setPatientPage({
                        page: 1
                    })
                );
                setFilterInputs(initialFilterValues);
                onChange(initialFilterValues);
                setIndicator(0);
            }
            if (pathname === '/settings/location' && from !== 'edit-location') {
                dispatch(
                    setLocationFilter({
                        status: [],
                        location_type: [],
                        search: ''
                    })
                );
                dispatch(
                    setLocationPage({
                        page: 1
                    })
                );
                setFilterInputs(initialFilterValues);
                onChange(initialFilterValues);
                setIndicator(0);
            }
            if (pathname === '/settings/employee' && from !== 'edit-employee') {
                dispatch(
                    setEmployeeFilter({
                        search: '',
                        role_id: [],
                        service_id: [],
                        active: [],
                        license: []
                    })
                );
                dispatch(
                    setEmployeePage({
                        page: 1
                    })
                );
                setFilterInputs(initialFilterValues);
                onChange(initialFilterValues);
                setIndicator(0);
            }
        }, [from]);

        useEffect(() => {
            if (pathname === '/patient' && from === 'edit-patient') {
                setFilterInputs({
                    active: prevfilters?.patients?.active,
                    locations: prevfilters?.patients?.locations,
                    search: prevfilters?.patients?.search
                });
            }
            if (pathname === '/settings/location' && from === 'edit-location') {
                setFilterInputs({
                    status: prevfilters?.locations?.status,
                    location_type: prevfilters?.locations?.locationType,
                    search: prevfilters?.locations?.search
                });
            }
            if (pathname === '/settings/employee' && from === 'edit-employee') {
                setFilterInputs({
                    search: prevfilters?.employee?.search,
                    role_id: prevfilters?.employee?.role_id,
                    service_id: prevfilters?.employee?.service_id,
                    active: prevfilters?.employee?.active,
                    license: prevfilters?.employee?.license
                });
            }
        }, []);

        useEffect(() => {
            setIndicator(0);

            if (pathname === '/patient' && from === 'edit-patient') {
                // const count = objectHasValue(prevfilters?.patients);
                const count = Object.entries(prevfilters?.patients || {}).filter(
                    ([key, value]) => key !== 'search' && value && value.length > 0
                ).length;
                setIndicator(count);
            }
            if (pathname === '/settings/location' && from === 'edit-location') {
                // const count = objectHasValue(prevfilters?.locations);
                const count = Object.entries(prevfilters?.locations || {}).filter(
                    ([key, value]) => key !== 'search' && value && value.length > 0
                ).length;
                setIndicator(count);
            }
            if (pathname === '/settings/employee' && from === 'edit-employee') {
                // const count = objectHasValue(prevfilters?.employee);
                const count = Object.entries(prevfilters?.employee || {}).filter(
                    ([key, value]) => key !== 'search' && value && value.length > 0
                ).length;
                setIndicator(count);
            }
        }, [pathname]);

        const handleFilterOpen = (e) => {
            setFilterAnchor(e.currentTarget);
        };
        const handleFilterClose = () => {
            setFilterAnchor(null);
            setErrors({});
        };

        // Outside click and the close button discard unsaved checks. Apply uses handleFilterClose so it is not reverted.
        const handleFilterDismiss = () => {
            if (isMasterDetail) {
                setFilterInputs({
                    ...(value || initialFilterValues),
                    search: filterInputs.search
                });
                setListKey((key) => key + 1);
            }
            handleFilterClose();
        };

        const handleFilterReset = () => {
            const keepSearch =
                pathname === '/patient' ||
                pathname === '/settings/employee' ||
                pathname === '/settings/eidt/classroom' ||
                pathname === '/settings/addt/classroom';
            const resetValues = keepSearch
                ? { ...initialFilterValues, search: filterInputs.search }
                : initialFilterValues;

            if (pathname === '/patient') {
                dispatch(
                    setPatientFilter({
                        active: [],
                        locations: [],
                        search: resetValues.search
                    })
                );

                dispatch(
                    setPatientPage({
                        page: 1
                    })
                );
            }
            if (pathname === '/settings/location') {
                dispatch(
                    setLocationFilter({
                        status: [],
                        location_type: [],
                        search: ''
                    })
                );
                dispatch(
                    setLocationPage({
                        page: 1
                    })
                );
            }

            if (pathname === '/settings/employee') {
                dispatch(
                    setEmployeeFilter({
                        search: resetValues.search,
                        role_id: [],
                        service_id: [],
                        active: [],
                        license: []
                    })
                );

                dispatch(
                    setEmployeePage({
                        page: 1
                    })
                );
            }
            handleFilterClose();
            setFilterInputs(resetValues);
            onChange(resetValues);
            setIndicator(0);
            if (isMasterDetail) {
                setListKey((key) => key + 1);
            }
        };

        useImperativeHandle(resetRef, () => ({
            handleFilterReset
        }));

        const handleCount = () => {
            setIndicator(indicator - 1);
            setFilterInputs(initialFilterValues);
        };

        useImperativeHandle(countRef, () => ({
            handleCount
        }));

        const handleFilterSearch = async () => {
            if (validationSchema) {
                const validate = await validationSchema.validate(filterInputs).catch((err) => err);
                if (validate.errors?.length > 0) {
                    notify({
                        message: validate.errors || 'Something went wrong',
                        severity: 'error'
                    });
                    return;
                }
            }

            if (Object.keys(errors).length > 0) {
                return;
            }

            onChange(filterInputs);
            handleFilterClose();

            setIndicator(getActiveFilterCount(filterInputs));
        };

        useEffect(() => {
            const handleSearch = setTimeout(() => {
                onChange(filterInputs);
            }, 500);

            return () => clearTimeout(handleSearch);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [filterInputs.search]);

        const filterSearch = filters?.filter((i) => i.type === 'text');
        const filterLists = filters?.filter((i) => i.type === 'checkbox');

        const hasLists = filterLists?.length > 0;
        // Fall back to the first group when no (or a stale) group is selected
        const activeFilterId = hasLists && filterLists.some((i) => i.id === activeGroupId) ? activeGroupId : filterLists?.[0]?.id;

        const getSelectionCount = (id) => {
            const val = filterInputs?.[id];
            if (Array.isArray(val)) return val.length;
            return val !== null && val !== undefined && val !== '' ? 1 : 0;
        };

        const handleChange = (key, value) => {
            const updateInputs = { ...filterInputs, [key]: value };
            setFilterInputs(updateInputs);
            if (dateRageKeys.includes(key) && updateInputs[firstDate] && updateInputs[secondDate]) {
                let isValid = false;
                if (key === firstDate) {
                    isValid = new Date(value) <= new Date(updateInputs[secondDate]);
                } else {
                    isValid = new Date(updateInputs[firstDate]) <= new Date(value);
                }
                if (!isValid) {
                    setErrors((prev) => ({
                        ...prev,
                        endDate: 'Start date cannot be greater than end date'
                    }));
                } else {
                    setErrors({});
                }
            }
        };

        return (
            <>
                <Stack
                    sx={{ m: 1 }}
                    direction="row"
                    alignItems="center"
                    flexWrap={isMasterDetail ? 'wrap' : 'nowrap'}
                    rowGap={isMasterDetail ? 1 : 0}
                >
                    {filterSearch.length > 0 && (
                        <FormControl
                            sx={
                                isMasterDetail
                                    ? { mr: { xs: 0, sm: 1 }, width: { xs: '100%', sm: 220 } }
                                    : { mr: 1, width: 200 }
                            }
                        >
                            <TextField
                                label={label}
                                type="search"
                                placeholder={placeholder}
                                variant="outlined"
                                size="small"
                                sx={{ my: 1 }}
                                disabled={disableSearch}
                                value={filterInputs.search}
                                onChange={(e) => {
                                    setFilterInputs((prev) => ({
                                        ...prev,
                                        search: e.target.value
                                    }));
                                }}
                            />
                        </FormControl>
                    )}
                    {!noFilterIcon ? (
                        <Tooltip title="Filters">
                            <IconButton
                                color="primary"
                                onClick={handleFilterOpen}
                                sx={isMasterDetail ? undefined : { position: 'relative' }}
                            >
                                {isMasterDetail ? (
                                    <Badge badgeContent={indicator} color="primary" invisible={!indicator}>
                                        <FilterAlt />
                                    </Badge>
                                ) : (
                                    <>
                                        {indicator ? (
                                            <Chip
                                                size="small"
                                                label={indicator}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    height: 20,
                                                    width: 20,
                                                    background: '#2196f3',
                                                    color: '#ffffff',
                                                    fontSize: 8,
                                                    borderRadius: 1,
                                                    '& span': {
                                                        p: 0
                                                    }
                                                }}
                                            />
                                        ) : null}
                                        <FilterAlt />
                                    </>
                                )}
                            </IconButton>
                        </Tooltip>
                    ) : null}
                </Stack>
                <Popover
                    open={Boolean(filterAnchor)}
                    anchorEl={filterAnchor}
                    onClose={handleFilterDismiss}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                    slotProps={
                        isMasterDetail
                            ? {
                                  paper: {
                                      sx: {
                                          border: (theme) => `1px solid ${theme.palette.divider}`
                                      }
                                  }
                              }
                            : undefined
                    }
                >
                    <Stack
                        direction="row"
                        alignItems={isMasterDetail ? 'center' : undefined}
                        sx={
                            isMasterDetail
                                ? {
                                      px: 2,
                                      py: 1.25,
                                      borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
                                      justifyContent: 'space-between'
                                  }
                                : {
                                      maxHeight: 50,
                                      p: 1,
                                      borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
                                      justifyContent: 'space-between'
                                  }
                        }
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TuneOutlined
                                sx={isMasterDetail ? { height: 20, width: 20, color: 'primary.main' } : { height: 24, width: 24 }}
                            />
                            <Typography sx={{ ml: 1, fontWeight: isMasterDetail ? 600 : undefined }} variant="subtitle1">
                                Filters
                            </Typography>
                        </Box>
                        <IconButton
                            size={isMasterDetail ? 'small' : undefined}
                            onClick={handleFilterDismiss}
                            aria-label="Close filters"
                        >
                            <Close sx={isMasterDetail ? { height: 20, width: 20 } : { height: 24, width: 24 }} />
                        </IconButton>
                    </Stack>
                    {isMasterDetail ? (
                    <Box sx={{ width: { xs: 'calc(100vw - 32px)', sm: hasLists ? 600 : 340 } }}>
                        {isLoading && hasLists ? (
                            <FilterSkeleton hasLists={hasLists} />
                        ) : (
                        <Stack direction={{ xs: 'column', sm: 'row' }}>
                            {(children || hasLists) && (
                                <Box
                                    sx={{
                                        width: hasLists ? { xs: '100%', sm: 200 } : '100%',
                                        flexShrink: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderColor: 'divider',
                                        borderRight: { sm: hasLists ? '1px solid' : 'none' },
                                        borderBottom: { xs: hasLists ? '1px solid' : 'none', sm: 'none' },
                                        maxHeight: { sm: hasLists ? 380 : 'none' },
                                        bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'transparent' : theme.palette.grey[50])
                                    }}
                                >
                                    {children && (
                                        <Box
                                            sx={{
                                                p: 1,
                                                flexShrink: 0,
                                                borderBottom: hasLists ? '1px solid' : 'none',
                                                borderColor: 'divider'
                                            }}
                                        >
                                            {Children.map(children, (child) => cloneElement(child, { value, onChange: handleChange }))}
                                            {Object.keys(errors).map((item) => (
                                                <FormHelperText key={item} sx={{ mt: 1 }} error>
                                                    {errors[item]}
                                                </FormHelperText>
                                            ))}
                                        </Box>
                                    )}
                                    {hasLists && (
                                        <Typography
                                            variant="overline"
                                            sx={{
                                                display: { xs: 'none', sm: 'block' },
                                                px: 2,
                                                pt: children ? 1 : 1.5,
                                                color: 'text.secondary',
                                                fontSize: 11,
                                                letterSpacing: 1
                                            }}
                                        >
                                            Filter by
                                        </Typography>
                                    )}
                                    {hasLists && (
                                        <List
                                            dense
                                            disablePadding
                                            sx={{
                                                display: 'flex',
                                                flexDirection: { xs: 'row', sm: 'column' },
                                                overflowX: { xs: 'auto', sm: 'hidden' },
                                                overflowY: { xs: 'hidden', sm: 'auto' },
                                                flex: { sm: 1 },
                                                minHeight: { sm: 0 },
                                                p: { xs: 1, sm: 0.5 }
                                            }}
                                        >
                                            {filterLists.map((item) => {
                                                const count = getSelectionCount(item.id);
                                                return (
                                                    <ListItemButton
                                                        key={item.id}
                                                        selected={item.id === activeFilterId}
                                                        onClick={() => setActiveGroupId(item.id)}
                                                        sx={{
                                                            'flex': '0 0 auto',
                                                            'borderRadius': 2,
                                                            'mx': 0.5,
                                                            'my': 0.25,
                                                            'px': 1.25,
                                                            'py': 0.75,
                                                            'whiteSpace': 'nowrap',
                                                            'transition': 'background-color 120ms ease',
                                                            '&.Mui-selected': {
                                                                'bgcolor': (theme) => alpha(theme.palette.primary.main, 0.08),
                                                                '&:hover': {
                                                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12)
                                                                },
                                                                '& .MuiListItemText-primary': {
                                                                    color: 'primary.main',
                                                                    fontWeight: 600
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={item.label}
                                                            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                                                        />
                                                        {count > 0 && (
                                                            <Chip
                                                                label={count}
                                                                size="small"
                                                                color="primary"
                                                                sx={{
                                                                    ml: 1,
                                                                    height: 18,
                                                                    minWidth: 18,
                                                                    '& .MuiChip-label': { px: 0.75, fontSize: 11 }
                                                                }}
                                                            />
                                                        )}
                                                    </ListItemButton>
                                                );
                                            })}
                                        </List>
                                    )}
                                </Box>
                            )}
                            {hasLists && (
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {filterLists.map((item) => (
                                        <Box key={item.id} sx={{ display: item.id === activeFilterId ? 'block' : 'none' }}>
                                            <CListGroup
                                                key={`${item.id}-${listKey}`}
                                                value={value?.[item.id]}
                                                name={item.label}
                                                multiple={item.multiple}
                                                options={item?.options || []}
                                                onChange={(val) => handleChange(item.id, val)}
                                                radio={item?.radio}
                                                groupBy={item?.groupBy}
                                                groupByKey={item?.groupByKey}
                                                variant={variant}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Stack>
                        )}
                        <Stack
                            direction="row"
                            alignItems="center"
                            sx={{
                                justifyContent: 'space-between',
                                borderTop: (theme) => `1px solid ${theme.palette.grey[300]}`,
                                px: 2,
                                py: 1.25,
                                flexShrink: 0
                            }}
                        >
                            <Button size="small" variant="outlined" onClick={handleFilterReset}>
                                Reset
                            </Button>
                            <Button size="small" variant="contained" onClick={handleFilterSearch} sx={{ px: 3 }}>
                                Apply
                            </Button>
                        </Stack>
                    </Box>
                    ) : (
                    <Grid
                        container
                        sx={{
                            maxWidth: 'calc(100vw / 2)',
                            minWidth: 300
                        }}
                    >
                        {isLoading && <Spinner />}
                        {children && (
                            <Grid
                                size={{ xs: 12, lg: 4 }}
                                sx={{
                                    maxHeight: 500,
                                    overflowY: 'auto',
                                    p: 1,
                                    borderRight: (theme) => `1px solid ${theme.palette.grey[300]}`
                                }}
                            >
                                {Children.map(children, (child) => cloneElement(child, { value, onChange: handleChange }))}
                                {Object.keys(errors).map((item) => (
                                    <FormHelperText key={item} sx={{ mt: 1 }} error>
                                        {errors[item]}
                                    </FormHelperText>
                                ))}
                            </Grid>
                        )}
                        <Grid size={{ xs: 12, lg: children ? 8 : 12 }} sx={{ overflowY: 'auto', maxHeight: 500 }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    flexWrap: 'wrap'
                                }}
                            >
                                {filterLists?.map((item, idx) => (
                                    <Box
                                        key={idx}
                                        sx={{
                                            flex: 1,
                                            minWidth: 300,
                                            maxWidth: 400,
                                            borderBottom: (theme) => `1px solid ${theme.palette.grey[300]}`,
                                            borderRight: (theme) => `1px solid ${theme.palette.grey[300]}`
                                        }}
                                    >
                                        <CListGroup
                                            value={value?.[item.id]}
                                            name={item.label}
                                            multiple={item.multiple}
                                            options={item?.options || []}
                                            onChange={(val) => handleChange(item.id, val)}
                                            radio={item?.radio}
                                            groupBy={item?.groupBy}
                                            groupByKey={item?.groupByKey}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                        <Grid size={12}>
                            <Stack
                                direction="row"
                                sx={{
                                    justifyContent: 'space-between',
                                    borderTop: (theme) => `1px solid ${theme.palette.grey[300]}`,
                                    p: 1,
                                    flexShrink: 0
                                }}
                            >
                                <Button size="small" variant="outlined" onClick={handleFilterReset}>
                                    Reset
                                </Button>
                                <Button size="small" variant="contained" onClick={handleFilterSearch}>
                                    Apply
                                </Button>
                            </Stack>
                        </Grid>
                    </Grid>
                    )}
                </Popover>
            </>
        );
    }
);

Datepicker.propTypes = {
    id: PropTypes.number,
    value: PropTypes.object,
    InputProps: PropTypes.object,
    containerProps: PropTypes.object,
    onChange: PropTypes.func
};

CListGroup.propTypes = {
    name: PropTypes.string,
    options: PropTypes.array,
    value: PropTypes.object,
    multiple: PropTypes.bool,
    onChange: PropTypes.func,
    radio: PropTypes.bool,
    variant: PropTypes.oneOf(['classic', 'master-detail'])
};

Root.propTypes = {
    isLoading: PropTypes.bool,
    value: PropTypes.object,
    filters: PropTypes.array,
    onChange: PropTypes.func,
    children: PropTypes.node,
    variant: PropTypes.oneOf(['classic', 'master-detail'])
};

export default { Root, Datepicker };
